import React from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
} from 'reactstrap';
import PropTypes from 'prop-types';
import {
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area,
} from 'recharts';
import {
  formatNumberWhole, formatDecimalToFixedTwoPercent,
} from '../../../../shared/components/table/functions';
import style from '../../../../shared/components/themeSupport';
import { currencyFormatter } from '../../../../shared/functions';
import CardPanel from '../../../../shared/components/widgets/CardPanel';
import {
  getTableHeaderStyle,
  getTableRowStyle,
  getTableWrapperStyle,
  isDarkTheme,
} from '../../../../shared/helpers/themeTableStyles';

function calcSubHeading(dateView) {
  let result = 'Daily';
  if (dateView && dateView.value === 'week') {
    result = 'Weekly';
  } else if (dateView && dateView.value === 'month') {
    result = 'Monthly';
  }
  return `${result} for Selected Date Range`;
}

function getItemDate(item) {
  return item.rankDate ? item.rankDate : item.displayDate;
}

export default function ExecutiveView({
  title, loading, account, data, dateView, isWalmart,
}) {
  const theme = useSelector(state => state.theme);
  const isDarkMode = isDarkTheme(theme);
  const isVendor = account && account.type === 2;
  const dateDataKey = isWalmart ? 'displayDate' : 'rankDate';

  return (
    <CardPanel
      title={title}
      parentRefresh={loading}
      showButtons={false}
      subhead={calcSubHeading(dateView)}
    >
      {!loading && data && (
        <>
          <div style={{
            marginTop: '30px',
          }}
          >
            <ResponsiveContainer height={400} className="dashboard__area">
              <ComposedChart
                width={600}
                height={400}
                data={data}
                margin={{
                  top: 5, right: 30, left: 20, bottom: 5,
                }}
              >
                <CartesianGrid stroke={style(theme).colorText} />
                <XAxis dataKey={dateDataKey} stroke={style(theme).colorText} />
                <YAxis dataKey="sales" yAxisId="left" stroke={style(theme).colorText} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="sales" yAxisId="left" formatter={value => currencyFormatter({ value, account })} stroke="green" name="Ad Sales" fill="#82ca9d" fillOpacity={0.3} />
                <Area type="monotone" dataKey="spend" yAxisId="left" formatter={value => currencyFormatter({ value, account })} stroke="purple" name="Ad Spend" fill="#8884d8" fillOpacity={0.3} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={{
            marginTop: '30px',
            marginLeft: '100px',
            ...getTableWrapperStyle(isDarkMode),
          }}
          >
            <Table responsive className="dashboard__table-orders" id="executiveSummary">
              <thead style={{ backgroundColor: isDarkMode ? '#232531' : '#f8f9fa' }}>
                <tr>
                  <th className="sticky-th" style={getTableHeaderStyle(isDarkMode)} />
                  {data.map(item => (
                    <th key={getItemDate(item)} style={getTableHeaderStyle(isDarkMode)}>{getItemDate(item)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>Ad Spend</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{currencyFormatter({ value: item.spend, account })}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, false)}>
                  <td className="sticky-td" style={getTableRowStyle(isDarkMode, false)}>Ad Sales</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, false)}>{currencyFormatter({ value: item.sales, account })}</td>
                  ))}
                </tr>
                {isVendor && (
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>Ord Rev</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{currencyFormatter({ value: item.ordered_revenue, account })}</td>
                  ))}
                </tr>
                )}
                {!isVendor && !isWalmart && (
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>Gross Sales</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{currencyFormatter({ value: item.gross_sales, account })}</td>
                  ))}
                </tr>
                )}
                <tr style={getTableRowStyle(isDarkMode, false)}>
                  <td className="sticky-td" style={getTableRowStyle(isDarkMode, false)}>PPC ACoS</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, false)}>{formatDecimalToFixedTwoPercent({ value: item.acos })}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>PPC ROAS</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{item.roas}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, false)}>
                  <td className="sticky-td" style={getTableRowStyle(isDarkMode, false)}>Clicks</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, false)}>{formatNumberWhole(item.clicks)}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>Impressions</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{formatNumberWhole(item.impressions)}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, false)}>
                  <td className="sticky-td" style={getTableRowStyle(isDarkMode, false)}>Orders</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, false)}>{formatNumberWhole(item.orders)}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>CVR</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{formatDecimalToFixedTwoPercent({ value: item.cvr })}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, false)}>
                  <td className="sticky-td" style={getTableRowStyle(isDarkMode, false)}>CTR</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, false)}>{formatDecimalToFixedTwoPercent({ value: (item.clicks / item.impressions) })}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>AOV</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{currencyFormatter({ value: (item.sales / item.orders), account })}</td>
                  ))}
                </tr>
                <tr style={getTableRowStyle(isDarkMode, false)}>
                  <td className="sticky-td" style={getTableRowStyle(isDarkMode, false)}>CPC</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, false)}>{currencyFormatter({ value: (item.spend / item.clicks), account })}</td>
                  ))}
                </tr>
                {!isVendor && !isWalmart && (
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>TACoS</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{formatDecimalToFixedTwoPercent({ value: (item.spend / item.gross_sales) })}</td>
                  ))}
                </tr>
                )}
                {isVendor && !isWalmart && (
                <tr style={getTableRowStyle(isDarkMode, true)}>
                  <td className="sticky-td-color" style={getTableRowStyle(isDarkMode, true)}>TACoS</td>
                  {data.map(item => (
                    <td key={getItemDate(item)} style={getTableRowStyle(isDarkMode, true)}>{formatDecimalToFixedTwoPercent({ value: (item.spend / item.ordered_revenue) })}</td>
                  ))}
                </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </CardPanel>
  );
}

ExecutiveView.defaultProps = {
  title: 'Executive Summary Data',
  data: null,
  dateView: { value: 'day' },
  isWalmart: false,
};

ExecutiveView.propTypes = {
  loading: PropTypes.bool.isRequired,
  account: PropTypes.instanceOf(Object).isRequired,
  title: PropTypes.string,
  data: PropTypes.instanceOf(Array),
  dateView: PropTypes.instanceOf(Object),
  isWalmart: PropTypes.bool,
};
